/**
 * Admin v2 · Anti-cheat settings.
 *
 * Renders the current suspicion-score weights + payout-curve thresholds
 * from GET /admin/settings/suspicion, lets an admin edit them inline,
 * and PATCHes back. All validation (weights sum, threshold ordering) is
 * server-side — the FE surfaces the error message as-is.
 *
 * Per-factor labels + descriptions are static here rather than served
 * from the API because they don't change without a code deploy anyway
 * (the factor list is part of the anti-cheat model, not tunable
 * configuration).
 */

import { api } from '../../api.js';
import { escape } from '../../components/form.js';
import { errorState } from './common.js';

const FACTOR_LABELS = {
  answer_time_anomaly: {
    label: 'Answer-time anomaly',
    hint: '% of answers below the P1 floor for their question. Strongest in-game signal.',
  },
  time_difficulty_decoupling: {
    label: 'Time × difficulty decoupling',
    hint: 'Humans slow on hard questions; bots do not.',
  },
  timing_uniformity: {
    label: 'Timing uniformity',
    hint: 'Low coefficient of variation — bot timings cluster tight.',
  },
  hard_accuracy_anomaly: {
    label: 'Hard-question accuracy',
    hint: 'Excess accuracy on top-25% difficulty band vs everyone else.',
  },
  new_account: {
    label: 'New account',
    hint: 'Hours since sign-up: <24h saturates.',
  },
  zero_practice: {
    label: 'Zero practice',
    hint: 'No solo/practice matches in the trivia\'s categories in past 30d.',
  },
  zero_powerup_engagement: {
    label: 'Zero power-up engagement',
    hint: 'No power-up grants (purchase / referral / awarded / admin) in past 30d.',
  },
};

export async function renderAntiCheat(host, ctx) {
  host.innerHTML = `<div style="padding:32px;color:var(--a-faint);">Loading…</div>`;
  let cfg;
  try {
    cfg = await api('/admin/settings/suspicion');
  } catch (err) {
    host.innerHTML = errorState(err);
    return;
  }
  _mount(host, ctx, cfg);
}

function _mount(host, ctx, cfg) {
  const weights = { ...cfg.weights };
  const thresholds = { ...cfg.thresholds };
  let enabled = !!cfg.enabled;

  host.innerHTML = `
    <div style="max-width:820px;">
      <div id="ac-status" class="adm2-notice" style="display:none;"></div>

      <div class="adm2-card" style="padding:20px;margin-bottom:16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:8px;">
          <div>
            <h3 style="margin:0;font-size:15px;">Pipeline</h3>
            <div style="color:var(--a-faint);font-size:12px;margin-top:2px;">
              Compute + apply the suspicion penalty at settle time.
              Disable to fall back to raw-points ranking.
            </div>
          </div>
          <label class="adm2-toggle">
            <input type="checkbox" id="ac-enabled" ${enabled ? 'checked' : ''} />
            <span>Enabled</span>
          </label>
        </div>
      </div>

      <div class="adm2-card" style="padding:20px;margin-bottom:16px;">
        <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:12px;">
          <div>
            <h3 style="margin:0;font-size:15px;">Factor weights</h3>
            <div style="color:var(--a-faint);font-size:12px;margin-top:2px;">
              Weights must sum to 1.00 (± 0.01). Each contribution feeds
              the composite score in [0, 1].
            </div>
          </div>
          <div style="text-align:right;">
            <div style="color:var(--a-faint);font-size:11px;letter-spacing:0.06em;text-transform:uppercase;">Total</div>
            <div id="ac-weight-total" class="mono" style="font-size:18px;color:var(--a-ok);">1.00</div>
          </div>
        </div>
        <div id="ac-weights" style="display:grid;gap:14px;">
          ${(cfg.factor_keys || Object.keys(FACTOR_LABELS)).map((k) => _renderWeightRow(k, weights[k])).join('')}
        </div>
      </div>

      <div class="adm2-card" style="padding:20px;margin-bottom:16px;">
        <h3 style="margin:0 0 4px;font-size:15px;">Penalty curve</h3>
        <div style="color:var(--a-faint);font-size:12px;margin-bottom:14px;">
          Piecewise concave. Score ≤ <b>low</b> → no penalty. Score ≥ <b>high</b> → 0 points.
          In between: <span class="mono">1 − ((s − low) / (high − low))^exponent</span>.
        </div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:14px;">
          ${_renderThresholdInput('low', 'Low (forgiven ≤)', thresholds.low, 0, 1, 0.01)}
          ${_renderThresholdInput('high', 'High (eliminated ≥)', thresholds.high, 0, 1, 0.01)}
          ${_renderThresholdInput('exponent', 'Curve exponent', thresholds.exponent, 0.1, 10, 0.1)}
        </div>
        <div id="ac-curve-preview" style="margin-top:14px;padding-top:12px;border-top:1px solid var(--a-border);"></div>
      </div>

      <div style="display:flex;gap:10px;">
        <button id="ac-save" class="adm2-btn adm2-btn-primary">Save changes</button>
        <button id="ac-reset" class="adm2-btn">Discard</button>
      </div>
    </div>
  `;

  // Wire inputs.
  host.querySelectorAll('input[data-weight]').forEach((el) => {
    el.addEventListener('input', () => {
      weights[el.dataset.weight] = Number(el.value) || 0;
      _updateTotal(host, weights);
    });
  });
  host.querySelectorAll('input[data-threshold]').forEach((el) => {
    el.addEventListener('input', () => {
      thresholds[el.dataset.threshold] = Number(el.value) || 0;
      _renderCurve(host, thresholds);
    });
  });
  host.querySelector('#ac-enabled').addEventListener('change', (e) => {
    enabled = !!e.target.checked;
  });
  host.querySelector('#ac-reset').addEventListener('click', () => {
    // Simplest reset — re-fetch from server. Avoids drift between the
    // in-memory copy and any admin-side defaults.
    renderAntiCheat(host, ctx);
  });
  host.querySelector('#ac-save').addEventListener('click', async () => {
    const btn = host.querySelector('#ac-save');
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const updated = await api('/admin/settings/suspicion', {
        method: 'PATCH',
        body: { weights, thresholds, enabled },
      });
      _showStatus(host, `Saved · weights sum ${_sum(updated.weights).toFixed(2)}`, 'ok');
      _mount(host, ctx, updated);
    } catch (err) {
      _showStatus(host, err.message || 'Save failed', 'err');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Save changes';
    }
  });

  _updateTotal(host, weights);
  _renderCurve(host, thresholds);
}

function _renderWeightRow(key, value) {
  const meta = FACTOR_LABELS[key] || { label: key, hint: '' };
  const v = value == null ? 0 : Number(value);
  return `
    <div style="display:grid;grid-template-columns:1fr 200px;gap:14px;align-items:center;">
      <div>
        <div style="font-weight:600;">${escape(meta.label)}</div>
        <div style="color:var(--a-faint);font-size:11.5px;line-height:1.4;margin-top:2px;">${escape(meta.hint)}</div>
      </div>
      <div style="display:flex;align-items:center;gap:8px;">
        <input type="range" min="0" max="1" step="0.01" value="${v.toFixed(2)}" data-weight="${escape(key)}"
               style="flex:1;" oninput="this.nextElementSibling.value = Number(this.value).toFixed(2);" />
        <input type="number" min="0" max="1" step="0.01" value="${v.toFixed(2)}" data-weight="${escape(key)}"
               class="mono" style="width:64px;text-align:right;"
               oninput="this.previousElementSibling.value = this.value;" />
      </div>
    </div>
  `;
}

function _renderThresholdInput(key, label, value, min, max, step) {
  const v = value == null ? 0 : Number(value);
  return `
    <div>
      <div style="color:var(--a-faint);font-size:11px;letter-spacing:0.06em;text-transform:uppercase;margin-bottom:4px;">${escape(label)}</div>
      <input type="number" min="${min}" max="${max}" step="${step}" value="${v}" data-threshold="${escape(key)}"
             class="mono" style="width:100%;font-size:14px;padding:8px;" />
    </div>
  `;
}

function _sum(obj) {
  return Object.values(obj || {}).reduce((a, b) => a + Number(b || 0), 0);
}

function _updateTotal(host, weights) {
  const total = _sum(weights);
  const el = host.querySelector('#ac-weight-total');
  if (!el) return;
  el.textContent = total.toFixed(2);
  // Same 0.99-1.01 tolerance the backend enforces on write. Under a
  // green pill when clean; red once the admin has drifted out of the
  // acceptable band so save-attempt would 400.
  const ok = Math.abs(total - 1.0) <= 0.01;
  el.style.color = ok ? 'var(--a-ok)' : 'var(--a-danger, #E94BA2)';
}

function _renderCurve(host, thresholds) {
  const wrap = host.querySelector('#ac-curve-preview');
  if (!wrap) return;
  // Sample the piecewise concave curve at 21 points and render as
  // simple mono-space bars. Keeps the visual dependency-free (no chart
  // lib) but immediately shows the shape for a given (low, high, exp).
  const low = Number(thresholds.low ?? 0.35);
  const high = Number(thresholds.high ?? 0.9);
  const exp = Number(thresholds.exponent ?? 1.5);
  const rows = [];
  for (let i = 0; i <= 20; i++) {
    const s = i / 20;
    let m;
    if (s <= low) m = 1.0;
    else if (s >= high) m = 0.0;
    else m = Math.max(0, Math.min(1, 1 - Math.pow((s - low) / (high - low), exp)));
    const bar = '█'.repeat(Math.round(m * 20));
    rows.push(`<div class="mono" style="font-size:11px;color:${_toneFor(m)};">${s.toFixed(2)} │ ${bar.padEnd(20, ' ')} │ ${m.toFixed(2)}</div>`);
  }
  wrap.innerHTML = `
    <div style="color:var(--a-faint);font-size:11px;letter-spacing:0.06em;text-transform:uppercase;margin-bottom:6px;">Preview · score → payout multiplier</div>
    <div style="white-space:pre;">${rows.join('')}</div>
  `;
}

function _toneFor(m) {
  if (m >= 0.99) return 'var(--a-ok)';
  if (m >= 0.5) return 'var(--a-warm-ink)';
  return 'var(--a-danger, #E94BA2)';
}

function _showStatus(host, msg, kind) {
  const el = host.querySelector('#ac-status');
  if (!el) return;
  el.textContent = msg;
  el.style.display = '';
  el.style.padding = '10px 14px';
  el.style.borderRadius = '6px';
  el.style.marginBottom = '14px';
  if (kind === 'ok') {
    el.style.background = 'rgba(43, 179, 155, 0.15)';
    el.style.border = '1px solid rgba(43, 179, 155, 0.4)';
    el.style.color = 'var(--a-ok)';
  } else {
    el.style.background = 'rgba(233, 75, 162, 0.12)';
    el.style.border = '1px solid rgba(233, 75, 162, 0.4)';
    el.style.color = 'var(--a-danger, #E94BA2)';
  }
}
